// The favorites block of a sidebar: items and one level of folders, in the order
// the person keeps. Only kinds a part registered are shown (a favorite of a part
// this app does not have stays stored but hidden). Rows are dragged into and out
// of folders and into their order (favorites-drag.ts), or moved by Alt+↑/↓.
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { type ComponentType, useState } from "react";
import { useMeApp } from "../app/context";
import { apiErrorWithLead } from "../data/errors";
import { isOptimisticId } from "../data/optimistic";
import {
  defineDeviceSetting,
  useDeviceSetting,
} from "../device/device-setting";
import { useListFlip } from "../shell/list-flip";
import { ROW, ROW_CURRENT, SectionHeading } from "../shell/nav-row";
import { ErrorLine, RowInput } from "../ui/bits";
import { Button } from "../ui/button";
import { confirmAction } from "../ui/confirm";
import { Caret, Icon } from "../ui/icon";
import { MenuButton } from "../ui/menu";
import { Modal } from "../ui/modal";
import {
  type FavoriteFolder,
  type FavoriteItem,
  useCreateFolder,
  useDeleteFolder,
  useFavorites,
  useMoveFavorite,
  useRenameFolder,
} from "./favorites";
import { useFavoriteDrag } from "./favorites-drag";
import type { FavoriteMove } from "./favorites-move";

/** Closed folders, remembered on this device. */
const closedFolders = defineDeviceSetting<readonly string[]>({
  key: "trameria.favorites.closed",
  parse: (raw) => {
    try {
      const v = JSON.parse(raw ?? "[]");
      return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  },
  serialize: (v) => JSON.stringify(v),
});

/** What a change to favorites reports, as a mutation does. */
export interface FavoriteChangeState<V> {
  mutate(input: V): void;
  isError: boolean;
  error: unknown;
  variables?: V;
}

/**
 * Where a favorites block gets its list and makes its changes: this app's
 * own (the API, `FavoritesSection`) or a list handed as data with the changes
 * sent back (a host drawing a place's sidebar, contract.ts `ShellFavorites`).
 */
export interface FavoritesSource {
  place: string;
  /** The entries in order; null while not read yet. */
  root: Array<FavoriteItem | FavoriteFolder> | null;
  /** An item's icon and where it opens; null for one not drawn here. */
  look(
    item: FavoriteItem,
  ): { icon: ComponentType<{ className?: string }>; to: string } | null;
  create: FavoriteChangeState<{ name: string }>;
  rename: FavoriteChangeState<{ id: string; name: string }>;
  remove: FavoriteChangeState<{ id: string }>;
  move: FavoriteChangeState<FavoriteMove>;
}

export function FavoritesSection({ place }: { place: string }) {
  const { favoriteKinds } = useMeApp();
  const list = useFavorites(place);
  const create = useCreateFolder(place);
  const rename = useRenameFolder(place);
  const remove = useDeleteFolder(place);
  const move = useMoveFavorite(place);
  if (Object.keys(favoriteKinds).length === 0) return null;
  return (
    <FavoritesView
      source={{
        place,
        root: list.data?.root ?? (list.isSuccess ? [] : null),
        look: (item) => {
          const kind = favoriteKinds[item.kind];
          return kind ? { icon: kind.icon, to: kind.to(item.targetId) } : null;
        },
        create,
        rename,
        remove,
        move,
      }}
    />
  );
}

/** The favorites block of a sidebar, from its source (see `FavoritesSource`). */
export function FavoritesView({ source }: { source: FavoritesSource }) {
  const { place, look, create, rename, remove, move } = source;
  const [naming, setNaming] = useState<
    { mode: "create" } | { mode: "rename"; folder: FavoriteFolder } | null
  >(null);
  const shown = (i: FavoriteItem) => look(i) !== null;
  const root = (source.root ?? []).flatMap<FavoriteItem | FavoriteFolder>(
    (e) =>
      e.type === "folder"
        ? [{ ...e, items: e.items.filter(shown) }]
        : shown(e)
          ? [e]
          : [],
  );
  const drag = useFavoriteDrag({ place, root }, (d) => move.mutate(d));
  // A failed create, delete or move has no row of its own (it went away on
  // rollback or is back in place): its line goes at the head of the list. A
  // failed rename goes under its folder.
  const folderName = (id: string) =>
    root.find((e): e is FavoriteFolder => e.type === "folder" && e.id === id)
      ?.name ?? "";
  const headFailure = create.isError
    ? apiErrorWithLead(
        create.error,
        m.shell_favorites_create_folder_failed({
          name: create.variables?.name ?? "",
        }),
      )
    : remove.isError
      ? apiErrorWithLead(
          remove.error,
          m.shell_favorites_delete_folder_failed({
            name: folderName(remove.variables?.id ?? ""),
          }),
        )
      : move.isError
        ? apiErrorWithLead(move.error, m.shell_favorites_move_failed())
        : null;
  return (
    <section aria-label={m.shell_favorites()}>
      <div className="group flex items-center">
        <SectionHeading>{m.shell_favorites()}</SectionHeading>
        <button
          type="button"
          aria-label={m.shell_favorites_new_folder()}
          className="mt-4 ml-auto inline-flex h-5 w-5 flex-none cursor-pointer items-center justify-center rounded-[5px] text-sidebar-muted opacity-0 hover:bg-sidebar-active group-hover:opacity-100 focus:opacity-100"
          onClick={() => setNaming({ mode: "create" })}
        >
          <Icon kind="plus" size={13} />
        </button>
      </div>
      {source.root !== null && root.length === 0 && (
        <p className="px-2.5 text-[12px] leading-[1.6] text-sidebar-muted">
          {m.shell_favorites_empty()}
        </p>
      )}
      {headFailure && (
        <div className="px-2.5 pb-1">
          <ErrorLine>{headFailure}</ErrorLine>
        </div>
      )}
      <div className="relative flex flex-col gap-px" {...drag.listProps}>
        {root.map((e) =>
          e.type === "folder" ? (
            <Folder
              key={e.id}
              folder={e}
              drag={drag}
              look={look}
              renameFailure={
                rename.isError && rename.variables?.id === e.id
                  ? apiErrorWithLead(
                      rename.error,
                      m.shell_favorites_rename_failed({ name: e.name }),
                    )
                  : null
              }
              onRename={() => setNaming({ mode: "rename", folder: e })}
              onDelete={async () => {
                const ok = await confirmAction({
                  title: m.shell_favorites_delete_folder_title({
                    name: e.name,
                  }),
                  body: m.shell_favorites_delete_folder_body(),
                  action: m.common_delete(),
                  danger: true,
                });
                if (ok) remove.mutate({ id: e.id });
              }}
            />
          ) : (
            <Item key={e.id} item={e} drag={drag} look={look} />
          ),
        )}
        {drag.lineAtEnd(null) && <DropLine edge="bottom" />}
        {/* The room under the list takes a drop too (the end of the root,
            even when the last row is a folder, whose lower half goes in).
            Out of the flow: the list keeps its height. */}
        {root.length > 0 && (
          <div aria-hidden="true" className="absolute inset-x-0 top-full h-2" />
        )}
      </div>
      <FolderNameModal
        key={
          naming
            ? naming.mode === "rename"
              ? naming.folder.id
              : "new"
            : "closed"
        }
        open={naming !== null}
        initial={naming?.mode === "rename" ? naming.folder.name : ""}
        onClose={() => setNaming(null)}
        onSubmit={(name) => {
          if (naming?.mode === "rename")
            rename.mutate({ id: naming.folder.id, name });
          else create.mutate({ name });
          setNaming(null);
        }}
      />
    </section>
  );
}

type Drag = ReturnType<typeof useFavoriteDrag>;

/**
 * Where a drop goes: a line on a row's edge, over the gap (no height of its
 * own). Inside a folder it starts where the folder's rows are indented, so in
 * and out of a folder read apart (the person, 2026-10-31).
 */
function DropLine({
  edge,
  inFolder = false,
}: {
  edge: "top" | "bottom";
  inFolder?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute right-2.5 h-0.5 rounded-full bg-accent ${
        inFolder ? "left-7" : "left-2.5"
      } ${edge === "top" ? "-top-px" : "-bottom-px"}`}
    />
  );
}

type Look = FavoritesSource["look"];

function Item({
  item,
  drag,
  look,
  last = false,
}: {
  item: FavoriteItem;
  drag: Drag;
  look: Look;
  /** The last in its folder: a drop at the folder's end draws its line here. */
  last?: boolean;
}) {
  const kind = look(item);
  if (!kind) return null;
  return <ItemRow item={item} drag={drag} kind={kind} last={last} />;
}

function ItemRow({
  item,
  drag,
  kind,
  last,
}: {
  item: FavoriteItem;
  drag: Drag;
  kind: NonNullable<ReturnType<Look>>;
  last: boolean;
}) {
  const KindIcon = kind.icon;
  const pending = isOptimisticId(item.id);
  const flip = useListFlip<HTMLAnchorElement>();
  const ref = { type: "item", id: item.id } as const;
  const indent = item.folderId !== null;
  return (
    <Link
      ref={flip}
      to={kind.to}
      disabled={pending}
      className={`${ROW} ${indent ? "pl-7" : ""} ${drag.isHeld(ref) ? "opacity-40" : ""}`}
      activeProps={{ className: ROW_CURRENT, "aria-current": "page" }}
      {...(pending
        ? { draggable: false }
        : { ...drag.rowProps(ref, item.folderId), ...drag.keyProps(ref) })}
    >
      {drag.lineBefore(ref, item.folderId) && (
        <DropLine edge="top" inFolder={indent} />
      )}
      {last && drag.lineAtEnd(item.folderId) && (
        <DropLine edge="bottom" inFolder={indent} />
      )}
      <KindIcon className="text-sidebar-muted" />
      <span className="min-w-0 flex-1 truncate">{item.title}</span>
    </Link>
  );
}

function Folder({
  folder,
  drag,
  look,
  renameFailure,
  onRename,
  onDelete,
}: {
  folder: FavoriteFolder;
  drag: Drag;
  look: Look;
  /** The line of a failed rename, shown right under the folder's row. */
  renameFailure: string | null;
  onRename(): void;
  onDelete(): void;
}) {
  const [closed, setClosed] = useDeviceSetting(closedFolders);
  const open = !closed.includes(folder.id);
  const flip = useListFlip<HTMLDivElement>();
  const ref = { type: "folder", id: folder.id } as const;
  const pending = isOptimisticId(folder.id);
  return (
    <div ref={flip}>
      <div
        className={`${ROW} pr-1 ${drag.intoFolder(folder.id) ? "bg-accent-soft" : ""} ${
          drag.isHeld(ref) ? "opacity-40" : ""
        }`}
        {...(pending ? {} : drag.rowProps(ref, null))}
      >
        {drag.lineBefore(ref, null) && <DropLine edge="top" />}
        <button
          type="button"
          aria-expanded={open}
          {...(pending ? {} : drag.keyProps(ref))}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left"
          onClick={() =>
            setClosed(
              open
                ? [...closed, folder.id]
                : closed.filter((id) => id !== folder.id),
            )
          }
        >
          <Icon
            kind={open ? "folder-open" : "folder-closed"}
            className="text-sidebar-muted"
          />
          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
          <span className="flex items-center text-sidebar-muted">
            <Caret open={open} />
          </span>
        </button>
        <MenuButton
          label={m.shell_favorites_folder_menu({ name: folder.name })}
          align="end"
          className="inline-flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-[5px] text-sidebar-muted opacity-0 group-hover:opacity-100 hover:bg-sidebar-active focus:opacity-100"
          items={[
            {
              id: "rename",
              label: m.shell_favorites_rename(),
              onSelect: onRename,
            },
            {
              id: "delete",
              label: m.shell_favorites_delete_folder(),
              danger: true,
              onSelect: onDelete,
            },
          ]}
        >
          <Icon kind="more" size={14} />
        </MenuButton>
      </div>
      {renameFailure && (
        <div className="px-2.5 pb-1">
          <ErrorLine>{renameFailure}</ErrorLine>
        </div>
      )}
      {open &&
        folder.items.map((i, n) => (
          <Item
            key={i.id}
            item={i}
            drag={drag}
            look={look}
            last={n === folder.items.length - 1}
          />
        ))}
    </div>
  );
}

function FolderNameModal({
  open,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initial: string;
  onClose(): void;
  onSubmit(name: string): void;
}) {
  const [name, setName] = useState(initial);
  const trimmed = name.trim();
  return (
    <Modal
      open={open}
      onClose={onClose}
      label={m.shell_favorites_folder_name()}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed) onSubmit(trimmed);
        }}
      >
        <label
          className="text-[12px] text-muted"
          htmlFor="favorite-folder-name"
        >
          {m.shell_favorites_folder_name()}
        </label>
        <RowInput
          id="favorite-folder-name"
          className="mt-1.5"
          value={name}
          maxLength={100}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {m.common_cancel()}
          </Button>
          <Button variant="primary" type="submit" disabled={!trimmed}>
            {m.common_save()}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
